import { z } from 'zod';
import { isoDateSchema } from '../common.js';
import { layoutSchema } from './layout.js';

export const pageSlugSchema = z
  .string()
  .trim()
  .min(1, 'Enter a slug.')
  .max(64)
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    'Use lowercase letters, numbers and dashes, e.g. getting-started.',
  );

/** Slugs the panel's own routes use; a custom page can't shadow them. */
export const RESERVED_PAGE_SLUGS = new Set([
  'admin',
  'account',
  'ai',
  'server',
  'login',
  'register',
  'overview',
  'custom',
  'api',
  'logout',
  'verify-email',
  'forgot-password',
  'reset-password',
  'assets',
]);

export const pageSettingsSchema = z.object({
  title: z.string().trim().min(1, 'Enter a title.').max(120),
  slug: pageSlugSchema,
  /** Who can open it: signed-in customers, or anyone (linked from the login page). */
  visibility: z.enum(['customers', 'public']).default('customers'),
  /** Show it in the sidebar (published pages only). */
  showInNav: z.boolean().default(true),
  navLabel: z.string().trim().max(40).default(''),
  navIcon: z
    .enum(['file-text', 'book', 'life-buoy', 'megaphone', 'users', 'rocket', 'shield', 'info'])
    .default('file-text'),
  navOrder: z.number().int().min(0).max(1000).default(100),
  description: z.string().trim().max(300).default(''),
});
export type PageSettings = z.infer<typeof pageSettingsSchema>;

/** The page document stored as draft and as published. */
export const pageContentSchema = z.object({
  version: z.literal(1),
  layout: layoutSchema,
  /** Page-level layout choices. */
  width: z.enum(['narrow', 'default', 'wide']).default('default'),
  showTitle: z.boolean().default(true),
});
export type PageContent = z.infer<typeof pageContentSchema>;

export const EMPTY_PAGE_CONTENT: PageContent = {
  version: 1,
  layout: { version: 1, nodes: [] },
  width: 'default',
  showTitle: true,
};

export const customPageSchema = pageSettingsSchema.extend({
  id: z.uuid(),
  draft: pageContentSchema,
  published: pageContentSchema.nullable(),
  /** The settings that were live when last published (slug and nav can differ in the draft). */
  publishedSettings: pageSettingsSchema.nullable(),
  publishedAt: isoDateSchema.nullable(),
  draftUpdatedAt: isoDateSchema,
  hasUnpublishedChanges: z.boolean(),
  createdAt: isoDateSchema,
});
export type CustomPage = z.infer<typeof customPageSchema>;

export const customPageSummarySchema = customPageSchema.omit({ draft: true, published: true });
export type CustomPageSummary = z.infer<typeof customPageSummarySchema>;

export const createPageSchema = pageSettingsSchema
  .partial({
    visibility: true,
    showInNav: true,
    navLabel: true,
    navIcon: true,
    navOrder: true,
    description: true,
  })
  .extend({
    draft: pageContentSchema.optional(),
  });

export const updatePageSchema = pageSettingsSchema.partial().extend({
  draft: pageContentSchema.optional(),
});

/** A page as a customer reads it. */
export const publishedPageSchema = z.object({
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  visibility: z.enum(['customers', 'public']),
  content: pageContentSchema,
  publishedAt: isoDateSchema.nullable(),
  /** True when the caller is reading the draft in preview mode. */
  preview: z.boolean(),
});
export type PublishedPage = z.infer<typeof publishedPageSchema>;

/** A nav entry for a published page. */
export const navPageSchema = z.object({
  slug: z.string(),
  label: z.string(),
  icon: pageSettingsSchema.shape.navIcon,
  visibility: z.enum(['customers', 'public']),
});
export type NavPage = z.infer<typeof navPageSchema>;
